<template>
  <span class="bulk-acl">
    <button
      type="button"
      class="bttn-bordered bttn-bordered--sm"
      :disabled="!selected.length"
      @click="open"
    >
      {{ $t("account.bulk_edit_permissions", "Bulk edit permissions")
      }}{{ selected.length ? ` (${selected.length})` : "" }}
    </button>

    <b-modal id="bulkSubAccountAcl" size="xl" scrollable @hidden="clearData">
      <div slot="modal-header">
        <h2>{{ $t("account.bulk_edit_permissions", "Bulk edit permissions") }}</h2>
      </div>

      <div slot="modal-footer">
        <button class="bttn bttn--lg bttn--blue" @click.prevent="hideModal">
          {{ $t("account.cancel") }}
        </button>
        <button
          class="bttn bttn--lg bttn--orange"
          :disabled="isLoading || !perLogin.length"
          @click.prevent="save"
        >
          {{ $t("account.save") }}
        </button>
      </div>

      <error-message ref="error"></error-message>

      <p class="text-muted">
        Editing permissions for {{ perLogin.length }} restricted account(s). Build a baseline
        permission set and apply it to everyone, then add/remove permissions and pick campaigns per
        account below. Saving replaces each account's permissions with exactly what is shown here,
        so removing a permission here removes it from that account.
      </p>

      <div class="bulk-acl__section">
        <h3 class="bulk-acl__heading">Baseline — applied to all selected</h3>
        <p class="text-muted small mb-2">
          Pick permissions to add to every selected account (skipped where already present).
          Campaigns are scoped per account below.
        </p>
        <div class="bulk-acl__baseline-list">
          <b-form-checkbox-group v-model="baselineActions" :options="aclItems" stacked />
        </div>
        <b-button
          variant="outline-primary"
          size="sm"
          class="mt-2"
          :disabled="!baselineActions.length"
          @click="applyBaselineToAll"
        >
          Apply baseline to all selected
        </b-button>
      </div>

      <hr />

      <div v-for="login in perLogin" :key="login.id" class="bulk-acl__login">
        <h4 class="bulk-acl__login-email">{{ login.email }}</h4>
        <div class="row">
          <restricted-acl-list v-model="login.acl" />
        </div>
      </div>
    </b-modal>
  </span>
</template>

<script>
import axios from "axios";
import urls from "@/constants/urls.js";
import { aclItems } from "@/constants/acls.js";
import {
  restrictedACLitemsToModelArr,
  restrictedACLModelArrToJSONApiRel,
} from "@/helpers/subAccounts";
import { notificationsMixin } from "@/mixins";

import errorList from "@/components/error-message.vue";
import RestrictedAclList from "@/components/restricted-acl-list.vue";

export default {
  name: "BulkSubAccountAcl",

  mixins: [notificationsMixin],

  components: {
    errorMessage: errorList,
    RestrictedAclList,
  },

  props: {
    // restricted sub-account objects selected in the list (jsonapi shape: id, attributes, relationships)
    selected: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      baselineActions: [],
      perLogin: [],
      isLoading: false,
    };
  },

  computed: {
    aclItems() {
      return aclItems;
    },
  },

  methods: {
    open() {
      if (!this.selected.length) {
        return;
      }

      this.baselineActions = [];
      // Preload each account's current permissions so they are visible and can be removed
      // (full-replace on save). Mirrors the per-account edit modal.
      this.perLogin = this.selected.map((acc) => ({
        id: acc.id,
        email: acc.attributes && acc.attributes.email,
        acl: restrictedACLitemsToModelArr(
          (acc.relationships && acc.relationships.restricted_acl_items.data) || []
        ),
      }));

      this.$root.$emit("bv::show::modal", "bulkSubAccountAcl");
    },

    clearData() {
      this.baselineActions = [];
      this.perLogin = [];
      this.isLoading = false;
    },

    hideModal() {
      this.$root.$emit("bv::hide::modal", "bulkSubAccountAcl");
    },

    // Add each baseline permission to every account that doesn't already have it (with no
    // campaign scope = all campaigns). Existing rows are left untouched, so accounts that
    // already have the action keep their per-account campaign scoping.
    applyBaselineToAll() {
      this.perLogin.forEach((login) => {
        const added = this.baselineActions
          .filter((action) => !login.acl.some((x) => x.action === action))
          .map((action) => ({ action, campaign_ids: [] }));

        if (added.length) {
          login.acl = [...login.acl, ...added];
        }
      });
    },

    // Flatten the picker model (action with campaign_ids) into the flat {action, campaign_id}
    // rows the bulk endpoint expects. Reuses the same expansion as the per-account save
    // (empty campaign_ids -> a single campaign_id 0 = all campaigns).
    aclToItems(acl) {
      const { included } = restrictedACLModelArrToJSONApiRel(acl || []);
      return included.map((i) => ({
        action: i.attributes.action,
        campaign_id: i.attributes.campaign_id,
      }));
    },

    save() {
      const orgId = localStorage.getItem("orgId");

      const payload = {
        accounts: this.perLogin.map((login) => ({
          account_id: Number(login.id),
          items: this.aclToItems(login.acl),
        })),
      };

      this.isLoading = true;
      axios
        .post(urls.bulkSubAccountAcl.replace(":orgId", orgId), payload)
        .then(() => {
          this.isLoading = false;
          this.$root.$emit("bv::hide::modal", "bulkSubAccountAcl");
          this.$store.dispatch("getSubaccountList");
          this.$_notificationsMixin_makeToast("Success", this.$t("account.success_edit"));
          this.$emit("saved");
        })
        .catch((e) => {
          this.isLoading = false;
          if (this.$refs.error) {
            this.$refs.error.setErrorResponse(e.response);
          }
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.bulk-acl {
  &__section {
    margin-bottom: 0.5rem;
  }

  &__heading {
    font-size: 1.1rem;
  }

  &__baseline-list {
    max-height: 180px;
    overflow-y: auto;
    padding: 8px 10px;
    border: 1px solid rgba(32, 67, 78, 0.15);
    border-radius: 4px;
  }

  &__login {
    padding: 0.75rem 0;
    border-top: 1px solid rgba(32, 67, 78, 0.1);

    &-email {
      font-size: 1rem;
      font-weight: 600;
      margin-bottom: 0.5rem;
    }
  }
}
</style>
